import { Link } from 'react-router-dom';
import { Facebook, MessageCircle, MapPin, Phone, Mail, Clock } from 'lucide-react';
import { SITE_NAME, SITE_TAGLINE } from '../lib/brand';
import { useCMS } from '../hooks/useCMS';
import { useWhatsAppNumber, waLink } from '../hooks/useWhatsAppNumber';

export default function Footer() {
  const currentYear = new Date().getFullYear();
  const { getValue } = useCMS();
  const whatsappNumber = useWhatsAppNumber();
  const whatsappReady = whatsappNumber.length >= 8;

  return (
    <footer className="bg-x1493-dark text-white/70 py-12 border-t border-white/10">
      <div className="max-w-6xl mx-auto px-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-8">
          <div>
            <div className="flex items-center gap-2.5 mb-4">
              <span className="w-10 h-10 rounded-xl bg-x1493-secondary text-x1493-dark font-black text-sm flex items-center justify-center">YYC</span>
              <div>
                <span className="text-lg font-bold text-white block leading-tight">{SITE_NAME}</span>
                <span className="text-[10px] text-x1493-secondary tracking-[0.22em] font-bold">PICK &amp; SHIP</span>
              </div>
            </div>
            <p className="text-sm leading-relaxed">
              {SITE_TAGLINE}
            </p>
          </div>

          <div>
            <h4 className="text-white font-bold mb-4">探索</h4>
            <ul className="space-y-2 text-sm">
              <li><Link to="/" className="hover:text-x1493-secondary transition">首頁</Link></li>
              <li><Link to="/story" className="hover:text-x1493-secondary transition">品牌故事</Link></li>
              <li><Link to="/store" className="hover:text-x1493-secondary transition">商店</Link></li>
              <li><Link to="/offers" className="hover:text-x1493-secondary transition">限時優惠</Link></li>
              <li><Link to="/shipping" className="hover:text-x1493-secondary transition">運送說明</Link></li>
              {/* 媒體報道：內容就緒後加回（media-coverage CMS） */}
            </ul>
          </div>

          {/* 合作專區（low-key：Footer only，Header 不加） */}
          <div>
            <h4 className="text-white font-bold mb-4">合作</h4>
            <ul className="space-y-2 text-sm">
              <li><Link to="/partner" className="hover:text-x1493-secondary transition">成為推廣夥伴</Link></li>
              <li><Link to="/b2b" className="hover:text-x1493-secondary transition">品牌入駐出海</Link></li>
            </ul>
          </div>

          <div>
            <h4 className="text-white font-bold mb-4">聯繫我們</h4>
            <ul className="space-y-2 text-sm">
              <li className="flex items-center gap-2">
                <MessageCircle size={16} className="text-x1493-secondary" />
                {whatsappReady ? (
                  <a href={waLink(whatsappNumber, '你好，我想查詢')} target="_blank" rel="noopener noreferrer" className="hover:text-x1493-secondary transition">
                    WhatsApp
                  </a>
                ) : (
                  <span className="text-white/50" title="WhatsApp 即將開通">WhatsApp（即將開通）</span>
                )}
              </li>
              <li className="flex items-center gap-2">
                <Phone size={16} className="text-x1493-secondary" />
                <a href={`tel:${getValue('footer.info.phone1', '+85292165198')}`} className="hover:text-x1493-secondary transition">
                  {getValue('footer.info.phone1_display', '(852) 9216-5198')}
                </a>
                <span className="text-white/30">｜</span>
                <a href={`tel:${getValue('footer.info.phone2', '+85256207644')}`} className="hover:text-x1493-secondary transition">
                  {getValue('footer.info.phone2_display', '(852) 5620-7644')}
                </a>
              </li>
              <li className="flex items-center gap-2">
                <Mail size={16} className="text-x1493-secondary" />
                <a href={`mailto:${getValue('footer.info.email', 'info@yycglobal.com')}`} className="hover:text-x1493-secondary transition">
                  {getValue('footer.info.email', 'info@yycglobal.com')}
                </a>
              </li>
              <li className="flex items-start gap-2">
                <MapPin size={16} className="text-x1493-secondary mt-0.5" />
                <span>{getValue('footer.info.address', '荃灣德士古道62-72號寶業大廈4樓（YYC Global Logistics）')}</span>
              </li>
              <li className="flex items-start gap-2">
                <Clock size={16} className="text-x1493-secondary mt-0.5" />
                <span>{getValue('footer.info.hours', '星期一至五 9:00–18:00｜星期六 9:00–13:00｜星期日及公眾假期休息')}</span>
              </li>
              <li className="flex items-center gap-2">
                <Facebook size={16} className="text-x1493-secondary" />
                <a
                  href="https://www.facebook.com/profile.php?id=61573736784763"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-x1493-secondary transition"
                >
                  Facebook
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-white/10 pt-6 flex flex-col md:flex-row justify-between items-center gap-4 text-xs">
          <p>© {currentYear} {SITE_NAME}</p>
          <div className="flex items-center gap-4">
            <Link to="/terms" className="hover:text-x1493-secondary transition">條款及細則</Link>
            <span className="text-white/30">|</span>
            <Link to="/privacy" className="hover:text-x1493-secondary transition">隱私條款</Link>
          </div>
          <p dangerouslySetInnerHTML={{ __html: getValue('footer.powered', 'Powered by <a href="https://jkdcoding.com/" target="_blank" rel="noopener noreferrer" class="font-bold text-yellow-300 hover:underline">JKDCoding</a>') }} />
        </div>
      </div>
    </footer>
  );
}
